import { computed, ref, watch, watchEffect } from 'vue';
import { HttpStatusCode } from 'axios';
import { useDocumentVisibility, useIntervalFn } from '@vueuse/core';

import { useHttp, useToasts } from '@/composables';

// Generation was measured at 22 to 114 seconds in production, so ten seconds is responsive without being
// chatty. Nothing polls unless a snapshot is actually generating.
const POLL_INTERVAL_MS = 10000;

export const usePlanPdf = (municipalityId) => {
  const toasts = useToasts();

  // keyed by workflowStep → { status: 'pending' | 'complete' | 'failed', stale: boolean, url: string|null }
  const planPdfs = ref({});

  const applySnapshots = (entries) => {
    const previous = planPdfs.value;

    // Entries arrive newest first and a step can repeat, because rejecting and resubmitting captures a
    // fresh snapshot. Keeping the first occurrence means a regenerating PDF shows as pending rather than
    // falling back to the previous one.
    planPdfs.value = entries.reduce((acc, { step, planPdf }) => {
      if (!acc[step]) {
        acc[step] = {
          status: planPdf.status,
          stale: planPdf.stale,
          url: planPdf.url,
        };
      }

      return acc;
    }, {});

    // Polling calls this every tick, so a failure has to be reported on the change into the failed state
    // rather than on the state itself. Comparing against the previous snapshot also means one that is
    // regenerated and fails again is still reported.
    const newFailure = Object.entries(planPdfs.value)
      .find(([step, planPdf]) => planPdf.status === 'failed' && previous[step]?.status !== 'failed');

    if (newFailure) {
      toasts.error('PDF generation failed. Please regenerate it from the workflow.');
    }
  };

  const setPlanPdfs = (transitions) => {
    applySnapshots(
      transitions
        .filter(transition => transition.planPdf)
        .map(transition => ({ step: transition.toStep, planPdf: transition.planPdf })),
    );
  };

  let refreshing = false;

  // Reads snapshot state on its own, rather than reloading the plan — whose payload carries the entire
  // IDP content document — just to find out whether generation has finished.
  const refreshSnapshots = async () => {
    // The interval does not wait for the previous request, so on a slow connection responses could
    // overlap and land out of order, briefly replacing current state with older state.
    if (refreshing) return;

    refreshing = true;

    try {
      const response = await useHttp().get(`/municipalities/${municipalityId}/pdf/snapshots`);

      if (response.statusCode === HttpStatusCode.Ok) {
        applySnapshots(response.data.snapshots ?? []);
      }
    } finally {
      refreshing = false;
    }
  };

  // Pending and not written off — generating now, or queued behind another municipality's. A stale snapshot
  // is still pending but has been given up on, so no further change is coming and polling for one would
  // never stop. Same condition the workflow actions gate themselves on.
  const isGenerating = planPdf => planPdf.status === 'pending' && !planPdf.stale;

  const hasPendingSnapshot = computed(() => Object.values(planPdfs.value).some(isGenerating));

  const visibility = useDocumentVisibility();

  const { pause, resume } = useIntervalFn(refreshSnapshots, POLL_INTERVAL_MS, { immediate: false });

  // Workflow actions at the MEC steps stay locked until their snapshot finishes generating, and the page
  // would otherwise never notice that it had. Polling stops as soon as nothing is generating, and while the
  // tab is hidden, so it lasts no longer than someone is actually waiting on it.
  watchEffect(() => {
    if (hasPendingSnapshot.value && visibility.value === 'visible') {
      resume();
    } else {
      pause();
    }
  });

  // The interval is paused while the tab is hidden, so coming back to it catches up immediately rather
  // than waiting out another full interval. Deliberately not conditional on anything already being
  // generating: an Administrator elsewhere may have submitted or regenerated in the meantime, and this
  // page would otherwise keep showing the state it last polled for.
  watch(visibility, (state) => {
    if (state === 'visible') refreshSnapshots();
  });

  const downloadForStep = (step) => {
    const url = planPdfs.value[step]?.url;

    if (!url) {
      toasts.error('The PDF could not be retrieved. Please refresh the page and try again.');
      return;
    }

    window.open(url, '_blank');
  };

  // Re-queues the plan's failed snapshot. Resolves true on success so the caller can reload the
  // plan and pick up the status flipping back to pending.
  const regenerate = async () => {
    const response = await useHttp().post(`/municipalities/${municipalityId}/plan/workflow/regenerate-pdf`);

    if (response.statusCode === HttpStatusCode.Ok) {
      toasts.success('Your PDF is being regenerated.');
      return true;
    }

    toasts.error('Failed to start PDF regeneration. Please try again.');
    return false;
  };

  return { planPdfs, setPlanPdfs, refreshSnapshots, downloadForStep, regenerate };
};
